import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { config } from '@/lib/config';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type SyncStatus = 'idle' | 'running' | 'done' | 'error';

export function SyncDialog({
  open,
  onOpenChange,
  type,
}: { open: boolean; onOpenChange: (o: boolean) => void; type: 'users' | 'drones' }) {
  const [lines, setLines] = useState<string[]>([]);
  const [status, setStatus] = useState<SyncStatus>('idle');
  const qc = useQueryClient();
  const cancelled = useRef(false);

  useEffect(() => {
    if (!open) {
      setLines([]);
      setStatus('idle');
      cancelled.current = false;
    }
  }, [open]);

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
  const log = (l: string) => setLines((p) => [...p, l]);

  const start = async () => {
    cancelled.current = false;
    setStatus('running');
    setLines([]);
    try {
      log('Connecting to airmarket.flysafe.software…');
      await sleep(400);
      if (cancelled.current) return;
      log('Authenticating with ApiKey…');
      await sleep(350);
      if (cancelled.current) return;
      log(`Fetching ${type}…`);
      await sleep(500);
      if (cancelled.current) return;
      const count = type === 'users' ? 12 : 8;
      log(`Found ${count} records`);
      for (let i = 1; i <= count; i++) {
        if (cancelled.current) return;
        await sleep(80);
        log(`  • Imported ${type === 'users' ? 'user' : 'drone'} #${i}`);
      }
      log('Sync complete.');
      qc.invalidateQueries({ queryKey: ['inventory', type] });
      setStatus('done');
    } catch (_e) {
      log('Sync failed.');
      setStatus('error');
    }
  };

  const cancel = () => {
    cancelled.current = true;
    setStatus('idle');
    log('Sync cancelled.');
  };

  /* The scripted log above is a MOCK-MODE ONLY demo — it sleeps and prints
     hardcoded "Imported drone #1..#8" lines without calling anything. In live
     mode that would be fabricated progress over real data, so the dialog
     shows a pending state instead: the Custodian API exposes no DroneLogBook
     sync trigger (the import runs backend-side, which is why records arrive
     with `imported_at` already set), so there is nothing to drive yet.
     Refresh below is the one real action available — it refetches the list
     and reports the count the API actually returned. */
  const refresh = async () => {
    setStatus('running');
    setLines([]);
    try {
      log('Refetching from the Custodian API…');
      await qc.refetchQueries({ queryKey: ['inventory', type] });
      const rows = qc.getQueryData<unknown[]>(['inventory', type, config.dataSource]);
      log(rows ? `Received ${rows.length} ${type} from the API.` : 'No records returned.');
      setStatus('done');
    } catch {
      log('Refresh failed.');
      setStatus('error');
    }
  };

  if (!config.isMock) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sync {type} from DroneLogBook</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2.5 rounded-md border border-(--color-amber)/40 bg-(--color-amber-bg) p-3">
            <Clock size={16} className="text-(--color-amber) shrink-0 mt-0.5" />
            <div className="space-y-1">
              <div className="text-[0.846em] font-bold text-(--color-amber)">
                Pending backend support
              </div>
              <p className="text-[0.769em] text-(--color-text2) leading-relaxed">
                {type === 'users'
                  ? 'The Custodian API is aircraft-only — it serves no users endpoint, so there is nothing to sync or refresh yet.'
                  : 'The DroneLogBook import runs backend-side and the Custodian API exposes no endpoint to trigger it. Wire this up once BE adds one.'}
              </p>
            </div>
          </div>
          {lines.length > 0 && (
            <pre className="rounded-md bg-(--color-bg) border border-(--color-border) p-2 font-mono text-[0.692em] max-h-[160px] overflow-y-auto">
              {lines.join('\n')}
            </pre>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button
              onClick={refresh}
              disabled={type === 'users' || status === 'running'}
              title={
                type === 'users'
                  ? 'No users endpoint to refresh from'
                  : 'Refetch the aircraft list from the Custodian API'
              }
            >
              {status === 'running' ? 'Refreshing…' : 'Refresh from API'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Sync {type} from DroneLogBook</DialogTitle>
        </DialogHeader>
        <pre
          className={cn(
            'rounded-md bg-(--color-bg) border border-(--color-border) p-2 font-mono text-[0.692em] min-h-[140px] max-h-[260px] overflow-y-auto',
            status === 'error' && 'border-(--color-red)/40',
            status === 'done' && 'border-(--color-green)/40',
          )}
        >
          {lines.length === 0 ? (
            <span className="text-(--color-text3)">Click "Start Sync" to begin.</span>
          ) : (
            lines.join('\n')
          )}
        </pre>
        <DialogFooter>
          {status === 'running' ? (
            <Button variant="ghost" onClick={cancel}>
              Cancel
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
          <Button onClick={start} disabled={status === 'running'}>
            {status === 'running' ? 'Syncing…' : status === 'done' ? 'Sync Again' : 'Start Sync'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
